/*
 * Westside custom: mobile quick-link strip (snippets/header.quick-links.liquid)
 *
 * - Keeps links in their configured order and wraps them into at most two rows.
 *   If two rows can't fit the screen, the list is widened to the narrowest width
 *   that still gives two rows, and the strip scrolls horizontally inside itself.
 * - Toggles data-overflow-start / data-overflow-end for the edge fade swipe hint.
 * - Publishes --mobile-quick-links-height so the sticky header reserves the space.
 */

class MobileQuickLinks extends HTMLElement {
  connectedCallback() {
    this.scroller = this.querySelector('.mobile-quick-links__scroller')
    this.list = this.querySelector('.mobile-quick-links__list')
    if (!this.scroller || !this.list) return

    this.abortController = new AbortController()
    const signal = this.abortController.signal

    this.update = this.update.bind(this)
    this.updateOverflow = this.updateOverflow.bind(this)

    this.resizeObserver = new ResizeObserver(() => this.schedule())
    this.resizeObserver.observe(this.scroller)

    this.scroller.addEventListener('scroll', this.updateOverflow, { passive: true, signal })

    // Keyboard: browsers skip scrolling a partly visible link into view, so do it here
    this.list.addEventListener('focusin', (event) => this.revealItem(event.target), { signal })

    // Label widths change once web fonts finish loading
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.schedule())
    }

    // Theme editor: bring the selected link into view
    document.addEventListener(
      'shopify:block:select',
      (event) => {
        const item = event.target
        if (item instanceof Element && this.contains(item)) {
          item.scrollIntoView({ block: 'nearest', inline: 'center' })
        }
      },
      { signal }
    )

    this.schedule()
  }

  disconnectedCallback() {
    this.abortController?.abort()
    this.resizeObserver?.disconnect()
    cancelAnimationFrame(this.frame)
    document.documentElement.style.removeProperty('--mobile-quick-links-height')
  }

  schedule() {
    cancelAnimationFrame(this.frame)
    this.frame = requestAnimationFrame(this.update)
  }

  update() {
    const listStyle = getComputedStyle(this.list)
    const paddingX = parseFloat(listStyle.paddingLeft) + parseFloat(listStyle.paddingRight)
    const available = this.scroller.clientWidth - paddingX

    // Hidden (desktop, compressed header, drawer open): keep the last good values
    if (this.scroller.clientWidth === 0 || available <= 0) return

    const items = Array.from(this.list.children)
    if (items.length === 0) return

    // Measure natural tag widths at the default (wrapping) width
    this.list.style.width = ''
    const gap = parseFloat(listStyle.columnGap) || 0
    const widths = items.map((item) => item.getBoundingClientRect().width)
    const total = widths.reduce((sum, w) => sum + w, 0) + gap * (items.length - 1)

    let width = ''

    if (total > available + 0.5) {
      // Narrowest width that splits the ordered list into two rows
      let needed = total
      let prefix = 0
      for (let i = 1; i < items.length; i++) {
        prefix += widths[i - 1] + (i > 1 ? gap : 0)
        const suffix = total - prefix - gap
        needed = Math.min(needed, Math.max(prefix, suffix))
      }

      // Greedy wrapping at this width can't produce more than two rows
      if (needed > available) {
        width = `${Math.ceil(needed) + 1}px`
      }
    }

    this.list.style.width = width
    this.toggleAttribute('data-scrollable', width !== '')

    const height = this.offsetHeight
    if (height > 0) {
      document.documentElement.style.setProperty('--mobile-quick-links-height', `${height}px`)
    }

    this.updateOverflow()
  }

  revealItem(target) {
    const item = target instanceof Element ? target.closest('.mobile-quick-links__item') : null
    if (!item) return

    const inset = parseFloat(getComputedStyle(this).getPropertyValue('--mql-fade')) || 0
    const view = this.scroller.getBoundingClientRect()
    const rect = item.getBoundingClientRect()
    let delta = 0

    if (rect.right > view.right - inset) delta = rect.right - (view.right - inset)
    if (rect.left < view.left + inset) delta = rect.left - (view.left + inset)

    if (delta !== 0) this.scroller.scrollLeft += delta
  }

  updateOverflow() {
    const max = this.scroller.scrollWidth - this.scroller.clientWidth
    const position = Math.abs(this.scroller.scrollLeft)
    this.toggleAttribute('data-overflow-start', max > 1 && position > 1)
    this.toggleAttribute('data-overflow-end', max > 1 && position < max - 1)
  }
}

if (!customElements.get('mobile-quick-links')) {
  customElements.define('mobile-quick-links', MobileQuickLinks)
}
